/**
 * Collector de retenção D1/D7 — implementa docs/systems/retention-telemetry.md.
 *
 * Escuta o bus `lura:telemetry` (lib/telemetry.ts) e persiste APENAS eventos da
 * allowlist em `telemetry_events` (1 doc por flush, ≤25 eventos — espelha o
 * padrão de carousel_events_batched). Além disso grava o heartbeat diário em
 * `telemetry_sessions/{uid}_{dayKey}` (máx 1 write/dia/usuário, dedup via
 * localStorage) — é a base do cálculo de D1/D7.
 *
 * Tudo best-effort: telemetria nunca derruba fluxo (catch silencioso em tudo).
 * Sem PII nas props (convenção do lib/telemetry.ts). TTL: sessions 180d,
 * events 90d (campo expireAt + policy gcloud — ver runbook de deploy).
 */

import {
  addDoc,
  collection,
  doc,
  serverTimestamp,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "./firebase/config";

import { detectPlatform } from "./telemetryPlatform";
import { nativePlatform } from "./platform/native";
import { TELEMETRY_EVENT_BUS_NAME, type TelemetryProps } from "./telemetry";
import { dayKeyBRClient, isRetentionEvent } from "./telemetryRetention";

// Mantém alinhado com firestore.rules (telemetry_events: events.size() <= 25)
const MAX_BUFFER_SIZE = 25;
const FLUSH_INTERVAL_MS = 15_000;
/** Cap diário de eventos persistidos por usuário (anti-ruído; spec §4.2). */
const MAX_EVENTS_PER_DAY = 50;

const SESSION_DAY_LS_KEY = "livra_tlm_session_day";
const EVENT_COUNT_LS_KEY = "livra_tlm_event_count";

const SESSIONS_TTL_MS = 180 * 24 * 60 * 60 * 1000;
const EVENTS_TTL_MS = 90 * 24 * 60 * 60 * 1000;


// ─── Estado do collector ──────────────────────────────────────────────────────

interface BufferedEvent {
  event: string;
  props: TelemetryProps;
  tsMs: number;
}

let currentUid: string | null = null;
const buffer: BufferedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let initialized = false;

function readDailyCount(dayKey: string): number {
  try {
    const raw = localStorage.getItem(EVENT_COUNT_LS_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw) as { day?: string; count?: number };
    return parsed.day === dayKey && typeof parsed.count === "number" ? parsed.count : 0;
  } catch {
    return 0;
  }
}

function bumpDailyCount(dayKey: string, by: number): void {
  try {
    localStorage.setItem(
      EVENT_COUNT_LS_KEY,
      JSON.stringify({ day: dayKey, count: readDailyCount(dayKey) + by }),
    );
  } catch {
    /* storage cheio/indisponível: segue sem cap persistido */
  }
}

// ─── Heartbeat diário (base do D1/D7) ────────────────────────────────────────

function maybeWriteSession(): void {
  const uid = currentUid;
  if (!uid) return;
  const today = dayKeyBRClient();
  try {
    if (localStorage.getItem(SESSION_DAY_LS_KEY) === `${uid}:${today}`) return;
  } catch {
    /* sem storage: tenta mesmo assim (rules create-only deduplicam) */
  }

  const mark = () => {
    try {
      localStorage.setItem(SESSION_DAY_LS_KEY, `${uid}:${today}`);
    } catch {
      /* ignore */
    }
  };

  setDoc(doc(db, "telemetry_sessions", `${uid}_${today}`), {
    uid,
    dayKey: today,
    platform: detectPlatform(),
    // iOS × Android (06/10): `platform` só diz `capacitor`, e a medição do
    // funil PRO precisou separar as lojas. `null` fora do app. As rules não
    // fecham as chaves deste doc, então o campo não pede deploy de rules.
    os: nativePlatform(),
    firstSeenAt: serverTimestamp(),
    expireAt: Timestamp.fromMillis(Date.now() + SESSIONS_TTL_MS),
  })
    .then(mark)
    .catch((e: unknown) => {
      // permission-denied = doc do dia já existe (create-only) → considera feito.
      const code = e && typeof e === "object" && "code" in e ? String((e as { code: unknown }).code) : "";
      if (code.includes("permission-denied")) mark();
    });
}

// ─── Flush dos eventos ────────────────────────────────────────────────────────

function flush(): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  const uid = currentUid;
  if (!uid || buffer.length === 0) return;
  const events = buffer.splice(0, buffer.length);
  const dayKey = dayKeyBRClient();

  addDoc(collection(db, "telemetry_events"), {
    uid,
    dayKey,
    events,
    ts: serverTimestamp(),
    expireAt: Timestamp.fromMillis(Date.now() + EVENTS_TTL_MS),
  })
    .then(() => bumpDailyCount(dayKey, events.length))
    .catch(() => undefined);
}

function scheduleFlush(): void {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

// ─── Init (chamar 1x no boot — main.tsx) ─────────────────────────────────────

export function initTelemetryCollector(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  onAuthStateChanged(auth, (user) => {
    currentUid = user?.uid ?? null;
    if (currentUid) maybeWriteSession();
  });

  window.addEventListener(TELEMETRY_EVENT_BUS_NAME, (e: Event) => {
    const detail = (e as CustomEvent<{ event?: string; props?: TelemetryProps }>).detail;
    const event = detail?.event;
    if (!event || !isRetentionEvent(event) || !currentUid) return;

    maybeWriteSession(); // qualquer atividade garante a sessão do dia

    const today = dayKeyBRClient();
    if (readDailyCount(today) + buffer.length >= MAX_EVENTS_PER_DAY) return;

    buffer.push({ event, props: detail?.props ?? {}, tsMs: Date.now() });
    if (buffer.length >= MAX_BUFFER_SIZE) flush();
    else scheduleFlush();
  });

  window.addEventListener("beforeunload", flush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
}
